import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AdminPage.css';

const ROLES = ['viewer', 'deposito', 'admin'];

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'viewer' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [whitelist, setWhitelist] = useState([]);
  const [wlLoading, setWlLoading] = useState(true);
  const [wlForm, setWlForm] = useState({ email: '', role: 'viewer' });
  const [wlAdding, setWlAdding] = useState(false);
  const [wlError, setWlError] = useState('');
  const [wlDeleteTarget, setWlDeleteTarget] = useState(null);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
    api.get('/users/whitelist').then(r => setWhitelist(r.data)).finally(() => setWlLoading(false));
  }, []);

  const handleRoleChange = async (userId, role) => {
    try {
      const { data } = await api.patch(`/users/${userId}`, { role });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...data } : u));
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleToggleActive = async (userId, active) => {
    try {
      const { data } = await api.patch(`/users/${userId}`, { active: !active });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...data } : u));
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const { data } = await api.delete(`/users/${deleteTarget.id}`);
      setUsers(prev => prev.map(u => u.id === data.id ? { ...u, ...data } : u));
      setDeleteTarget(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al desactivar');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      const { data } = await api.post('/auth/users', form);
      setUsers(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setShowCreate(false);
      setForm({ email: '', name: '', password: '', role: 'viewer' });
    } catch (e) {
      setCreateError(e.response?.data?.error || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const handleAddWhitelist = async (e) => {
    e.preventDefault();
    setWlAdding(true);
    setWlError('');
    try {
      const { data } = await api.post('/users/whitelist', wlForm);
      setWhitelist(prev => [...prev, data].sort((a, b) => a.email.localeCompare(b.email)));
      setWlForm({ email: '', role: 'viewer' });
    } catch (e) {
      setWlError(e.response?.data?.error || 'Error al agregar');
    } finally {
      setWlAdding(false);
    }
  };

  const handleDeleteWhitelist = async () => {
    try {
      await api.delete(`/users/whitelist/${wlDeleteTarget.id}`);
      setWhitelist(prev => prev.filter(w => w.id !== wlDeleteTarget.id));
      setWlDeleteTarget(null);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Administración</h1>
      </div>

      {/* ── Usuarios ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Usuarios</h2>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowCreate(v => !v); setCreateError(''); }}>
          {showCreate ? 'Cancelar' : '+ Agregar usuario'}
        </button>
      </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 24, maxWidth: 480 }}>
          <div className="modal-title" style={{ fontSize: 16, marginBottom: 16 }}>Nuevo usuario</div>
          <form onSubmit={handleCreate}>
            {createError && <div className="login-error" style={{ marginBottom: 12 }}>{createError}</div>}
            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <input className="input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Rol</label>
              <select className="input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-8 text-dim"><div className="spinner" style={{ width: 20, height: 20 }} /> Cargando...</div>
      ) : (
        <div className="users-table">
          <div className="users-header">
            <span>Usuario</span>
            <span>Rol</span>
            <span>Estado</span>
            <span>Último acceso</span>
            <span></span>
          </div>
          {users.map(u => (
            <div key={u.id} className={`user-row${!u.active ? ' inactive' : ''}`}>
              <div className="user-cell">
                {u.avatar_url
                  ? <img src={u.avatar_url} alt="" className="user-avatar" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                  : <div className="user-avatar-fallback" style={{ width: 32, height: 32 }}>{u.name[0]}</div>
                }
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                </div>
              </div>
              <div className="user-cell">
                <select
                  className="input"
                  style={{ width: 'auto', padding: '4px 8px', fontSize: 13 }}
                  value={u.role}
                  onChange={e => handleRoleChange(u.id, e.target.value)}
                >
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="user-cell">
                <button
                  className={`badge ${u.active ? 'badge-green' : 'badge-gray'}`}
                  style={{ cursor: 'pointer', border: 'none' }}
                  onClick={() => handleToggleActive(u.id, u.active)}
                >
                  {u.active ? 'Activo' : 'Inactivo'}
                </button>
              </div>
              <div className="user-cell text-dim text-sm">
                {u.last_login ? new Date(u.last_login).toLocaleDateString('es') : '—'}
              </div>
              <div className="user-cell">
                {u.id !== currentUser?.id && (
                  u.active ? (
                    <button
                      className="btn btn-icon btn-ghost"
                      style={{ color: 'var(--red)', fontSize: 16 }}
                      onClick={() => setDeleteTarget(u)}
                      title="Desactivar usuario"
                    >
                      ✕
                    </button>
                  ) : (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ fontSize: 12, color: 'var(--accent)' }}
                      onClick={() => handleToggleActive(u.id, u.active)}
                      title="Reactivar usuario"
                    >
                      Reactivar
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Acceso autorizado (whitelist) ── */}
      <div style={{ marginTop: 48, marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Acceso autorizado</h2>
        <p className="text-dim text-sm" style={{ marginTop: 4 }}>Emails que pueden registrarse vía Google</p>
      </div>

      <div className="card" style={{ marginBottom: 24, maxWidth: 560 }}>
        <form onSubmit={handleAddWhitelist} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {wlError && <div className="login-error" style={{ width: '100%', marginBottom: 4 }}>{wlError}</div>}
          <div className="form-group" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
            <label className="form-label">Email</label>
            <input
              className="input"
              type="email"
              placeholder="usuario@ejemplo.com"
              value={wlForm.email}
              onChange={e => setWlForm(f => ({ ...f, email: e.target.value }))}
              required
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Rol</label>
            <select
              className="input"
              style={{ width: 'auto' }}
              value={wlForm.role}
              onChange={e => setWlForm(f => ({ ...f, role: e.target.value }))}
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={wlAdding} style={{ alignSelf: 'flex-end' }}>
            {wlAdding ? 'Agregando...' : '+ Agregar'}
          </button>
        </form>
      </div>

      {wlLoading ? (
        <div className="flex items-center gap-8 text-dim"><div className="spinner" style={{ width: 20, height: 20 }} /> Cargando...</div>
      ) : whitelist.length === 0 ? (
        <p className="text-dim text-sm">No hay emails en la lista.</p>
      ) : (
        <div className="users-table">
          {whitelist.map(w => (
            <div key={w.id} className="wl-row">
              <div className="wl-email">{w.email}</div>
              <div className="user-cell">
                <button
                  className="btn btn-icon btn-ghost"
                  style={{ color: 'var(--red)', fontSize: 16 }}
                  onClick={() => setWlDeleteTarget(w)}
                  title="Revocar acceso"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: desactivar usuario */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">¿Desactivar usuario?</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
              El usuario no podrá iniciar sesión pero sus datos se conservan:
            </p>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{deleteTarget.name}</p>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 24, wordBreak: 'break-all' }}>{deleteTarget.email}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancelar</button>
              <button className="btn btn-danger" onClick={handleDeleteUser}>Desactivar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: revocar acceso whitelist */}
      {wlDeleteTarget && (
        <div className="modal-overlay" onClick={() => setWlDeleteTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">¿Revocar acceso?</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
              Este email ya no podrá registrarse vía Google:
            </p>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, fontWeight: 600, marginBottom: 24, wordBreak: 'break-all' }}>
              {wlDeleteTarget.email}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setWlDeleteTarget(null)}>Cancelar</button>
              <button className="btn btn-danger" onClick={handleDeleteWhitelist}>Revocar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
